/**
 * Uploads a file (PDF / HTML / image) to the demo portal upload endpoint.
 * The request is same-origin, so the httpOnly __session cookie is sent
 * automatically and verified server-side by demoUploadApi.
 */

export interface UploadResult {
  url: string;            // gated path, e.g. /uploads/<id>/<name>
  thumbnailUrl: string | null; // gated path to generated thumbnail, or null
  fileType: 'image' | 'pdf' | 'html';
  fileName: string;
}

export type UploadError = { kind: 'unauthorized' } | { kind: 'unknown'; message: string };

export type UploadResponse =
  | { ok: true; data: UploadResult }
  | { ok: false; error: UploadError };

export async function uploadFile(file: File): Promise<UploadResponse> {
  const form = new FormData();
  form.append('file', file);

  let response: Response;
  try {
    response = await fetch('/api/upload', {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
    });
  } catch {
    return { ok: false, error: { kind: 'unknown', message: 'Network error' } };
  }

  if (response.ok) {
    const data = (await response.json()) as UploadResult;
    return { ok: true, data };
  }

  if (response.status === 401) {
    return { ok: false, error: { kind: 'unauthorized' } };
  }

  let message = `Upload failed (HTTP ${response.status})`;
  try {
    const body = await response.json();
    message = body?.error ?? message;
  } catch {
    // ignore
  }
  return { ok: false, error: { kind: 'unknown', message } };
}

/** True for gated paths produced by this uploader (e.g. /uploads/<uuid>/file.pdf). */
export function isUploadPath(url?: string): boolean {
  return !!url && /^\/uploads\/[0-9a-fA-F-]{36}\/[^/]+$/.test(url);
}

/** The /uploads/<uuid>/ folder prefix of an upload path, or null. */
function uploadFolder(url?: string): string | null {
  if (!isUploadPath(url)) return null;
  return url!.split('/').slice(0, 3).join('/'); // ['', 'uploads', '<uuid>']
}

/** True when two upload paths live in the same uploads/<uuid>/ folder. */
export function sharesUploadFolder(a?: string, b?: string): boolean {
  const fa = uploadFolder(a);
  const fb = uploadFolder(b);
  return fa !== null && fa === fb;
}

/**
 * Delete an uploaded object's folder (file + thumbnail). Best-effort: callers
 * should not block UX on the result. No-op for non-upload URLs.
 */
export async function deleteUpload(path: string): Promise<void> {
  if (!isUploadPath(path)) return;
  try {
    await fetch('/api/upload', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path }),
      credentials: 'same-origin',
    });
  } catch {
    // best-effort cleanup; ignore failures
  }
}
